import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import Footer from '@/components/atoms/Footer';
import Logo from '@/components/atoms/Logo';
import LogoutButton from '@/components/atoms/LogoutButton';
import useLogout from '@/components/hooks/useLogout';
import { useUserPermission } from '@/components/hooks/useUserPermission';
import { RoomIndexTab } from '@/components/molecules/admin/RoomIndexTab';
import { useNotificationsQuery } from '@/types/graphql';
import { AdminRoomTab } from '@/utils/constants';
const AdminRoomLayout = (props: { selectedTab?: AdminRoomTab }) => {
  const navigate = useNavigate();
  const userPermission = useUserPermission();
  const { t } = useTranslation('admin');
  const logout = useLogout();

  const notifications = useNotificationsQuery({
    variables: {
      input: {
        filterOnlyEnabled: true,
        ownerCompanyId: userPermission.ownerCompanyId,
        tenantCompanyId: userPermission.tenantCompanyId,
      },
    },
    pollInterval: 1000 * 30,
  });
  const notificationCount = notifications.data?.notifications.length ?? 0;
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack bgcolor={(t) => t.palette.background.default} minHeight="100vh">
      {/* logo header */}
      <Stack
        pt={{ xs: 1, sm: 2 }}
        px={{ xs: 1, sm: 2 }}
        width={1}
        direction="row"
        justifyContent="space-between"
      >
        <Logo sx={{ mx: 1 }} />
        <Stack direction="row" spacing={isXs ? 0 : 0.5}>
          {userPermission.isTenantCompanyAdmin && (
            <Button
              variant="text"
              sx={{
                minWidth: 0,
                px: 1,
                fontSize: isXs ? '60%' : undefined,
                whiteSpace: 'nowrap',
              }}
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/admin/user')}
            >
              {t('userSettings.userSettings')}
            </Button>
          )}
          <LogoutButton
            variant="text"
            sx={{
              minWidth: 0,
              px: 1,
              fontSize: isXs ? '60%' : undefined,
              whiteSpace: 'nowrap',
            }}
            onClick={() => logout()}
          />

          <IconButton
            sx={{ fontSize: isXs ? '60%' : undefined }}
            color="primary"
            size="small"
            onClick={(ev) => {
              if (notificationCount >= 1) setAnchorEl(ev.currentTarget);
            }}
          >
            <Badge badgeContent={notificationCount} color="info">
              <NotificationsIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={anchorEl != null}
          onClose={() => setAnchorEl(undefined)}
        >
          {notifications.data?.notifications.map((x) => (
            <MenuItem key={x.id} sx={{ whiteSpace: 'pre-wrap' }}>
              {x.message}
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      {/* admin body */}
      <Stack
        direction={'row'}
        sx={{ width: '100%', height: '100%', flex: 1, mt: { xs: 2, sm: 4 } }}
      >
        <Box
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '100%',
            height: 'max-content',
          }}
        >
          <Stack sx={{ pb: 2 }} alignItems={'center'}>
            <Box
              sx={{
                width: 1,
                paddingX: { xs: '16px', sm: '40px' },
              }}
            >
              <RoomIndexTab
                selected={props.selectedTab ?? AdminRoomTab.management}
                tab1={{
                  label: t('room.management'),
                  redirectTo: '/admin/room',
                }}
                tab2={{
                  label: t('template.admin'),
                  redirectTo: '/admin/template',
                }}
                tab3={{
                  label: t('homophone.correctionRecordAdmin'),
                  redirectTo: '/admin/homophone',
                }}
              />
              <Box
                sx={{
                  backgroundColor: (t) => t.palette.common.white,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                }}
              >
                <Outlet />
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>

      {/* hooter */}
      <Footer />
    </Stack>
  );
};
export default AdminRoomLayout;
